import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Shield, Loader2, Mail } from 'lucide-react';
import useAuthStore from '../store/authStore';
import { getTenantIdentifier } from '../contexts/TenantContext';
import Roles from '../constants/roles';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { trackEvent } from '../contexts/PostHogProvider';
import { trackAuthEvent } from '../utils/analyticsHelper';
import { AUTH_LOGIN_STARTED, AUTH_LOGIN_SUCCEEDED, AUTH_LOGIN_FAILED, FLOW_VARIANTS } from '../constants/analyticsEvents';
import configs from '../config';

const PropelLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none" className="w-10 h-10">
    <path d="M6 26L14 6L17 15L20 6L28 26L20 21L17 26L14 21Z" fill="#4F46E5" opacity="0.95"/>
    <path d="M14 6L17 15L20 6L17 10Z" fill="#6366F1"/>
  </svg>
);

export function LoginPage() {
  const [step, setStep] = useState('workspace'); // 'workspace' | 'credentials'
  const [workspace, setWorkspace] = useState('');
  const [verifiedWorkspaceSlug, setVerifiedWorkspaceSlug] = useState('');
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [tenantName, setTenantName] = useState('');
  const [tenantBranding, setTenantBranding] = useState(null);
  const [magicLinkSending, setMagicLinkSending] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { tenantSlug } = useParams();
  const { login, isAuthenticated, hasHydrated, user } = useAuthStore();

  useDocumentTitle('Sign In - Propel');

  const verifyTenant = async (slug) => {
    const res = await fetch(`${configs.BACKEND_URL}/api/tenant/verify/${encodeURIComponent(slug.trim().toLowerCase())}`);
    if (!res.ok) throw new Error('Unable to verify workspace');
    return res.json();
  };

  useEffect(() => {
    // Priority 1: If tenant slug in URL (e.g., /acme-corp/login), verify then skip to credentials
    if (tenantSlug) {
      const normalizedSlug = tenantSlug.trim().toLowerCase();
      setWorkspace(normalizedSlug);
      setIsVerifying(true);
      verifyTenant(normalizedSlug)
        .then((data) => {
          if (!data.exists) {
            trackEvent('tenant_verify_failed', { source: 'url', reason: 'not_found', slug: normalizedSlug });
            setError('Workspace not found. Please check the workspace name and try again.');
          } else if (!data.isActive) {
            trackEvent('tenant_verify_failed', { source: 'url', reason: 'inactive', slug: normalizedSlug });
            setError('This workspace is currently inactive. Contact your administrator.');
          } else {
            trackEvent('tenant_verify_success', { source: 'url', slug: normalizedSlug });
            setTenantName(data.name || normalizedSlug);
            setTenantBranding(data.branding || null);
            setVerifiedWorkspaceSlug(normalizedSlug);
            setStep('credentials');
          }
        })
        .catch(() => {
          trackEvent('tenant_verify_error', { source: 'url', slug: normalizedSlug });
          setError('Unable to verify workspace. Please try again.');
        })
        .finally(() => setIsVerifying(false));
      return;
    }

    // Priority 2: If no URL slug, try auto-verify from localStorage (fast path)
    const storedSlug = getTenantIdentifier();
    if (storedSlug) {
      setWorkspace(storedSlug);
      setIsVerifying(true);
      verifyTenant(storedSlug)
        .then((data) => {
          if (data.exists && data.isActive) {
            trackEvent('tenant_verify_success', { source: 'stored', slug: storedSlug });
            setTenantName(data.name || storedSlug);
            setTenantBranding(data.branding || null);
            setVerifiedWorkspaceSlug(storedSlug);
            setStep('credentials');
          } else {
            // Stored tenant is invalid/inactive — clear it and show workspace entry
            trackEvent('tenant_verify_failed', { 
              source: 'stored', 
              reason: data.exists ? 'inactive' : 'not_found', 
              slug: storedSlug 
            });
            try { localStorage.removeItem('tenant-slug'); } catch {}
            setWorkspace('');
          }
        })
        .catch(() => {
          // Network error or server issue — fail gracefully, user can manually enter workspace
          trackEvent('tenant_verify_error', { source: 'stored', slug: storedSlug });
          setWorkspace(storedSlug); // Keep it filled for manual retry
        })
        .finally(() => setIsVerifying(false));
    }
  }, [tenantSlug]);

  useEffect(() => {
    // Redirect authenticated users
    if (hasHydrated && isAuthenticated && user) {
      // Check for redirect query parameter (e.g., from OAuth flow)
      const redirectParam = new URLSearchParams(location.search).get('redirect');
      if (redirectParam) {
        if (redirectParam.startsWith('http')) {
          window.location.href = redirectParam;
        } else if (redirectParam.startsWith('/')) {
          navigate(redirectParam, { replace: true });
        }
        return;
      }
      if (user.role === 'owner') {
        navigate('/owner/tenants', { replace: true });
      } else if (user.role === Roles.ADMIN) {
        navigate('/admin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, hasHydrated, user, navigate, location.search]);

  const from = location.state?.from?.pathname || '/dashboard';
  const workspaceSlugForLinks = (verifiedWorkspaceSlug || tenantSlug || workspace || '').trim().toLowerCase();
  const forgotPasswordPath = !isPlatformAdmin && workspaceSlugForLinks
    ? `/${workspaceSlugForLinks}/forgot-password`
    : '/forgot-password';

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!workspace.trim()) {
      setError('Please enter your workspace name');
      return;
    }
    setError(null);
    setIsVerifying(true);
    try {
      const normalizedSlug = workspace.trim().toLowerCase();
      const data = await verifyTenant(normalizedSlug);
      if (!data.exists) {
        trackEvent('tenant_verify_failed', { source: 'manual', reason: 'not_found', slug: normalizedSlug });
        setError('Workspace not found. Please check the workspace name and try again.');
      } else if (!data.isActive) {
        trackEvent('tenant_verify_failed', { source: 'manual', reason: 'inactive', slug: normalizedSlug });
        setError('This workspace is currently inactive. Contact your administrator.');
      } else {
        trackEvent('tenant_verify_success', { source: 'manual', slug: normalizedSlug });
        setWorkspace(normalizedSlug);
        setTenantName(data.name || normalizedSlug);
        setTenantBranding(data.branding || null);
        setVerifiedWorkspaceSlug(normalizedSlug);
        setStep('credentials');
      }
    } catch {
      trackEvent('tenant_verify_error', { source: 'manual', slug: workspace.trim().toLowerCase() });
      setError('Unable to verify workspace. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePlatformAdmin = () => {
    setIsPlatformAdmin(true);
    setWorkspace('');
    setVerifiedWorkspaceSlug('');
    setTenantName('');
    setError(null);
    setStep('credentials');
  };

  const handleBack = () => {
    setStep('workspace');
    setIsPlatformAdmin(false);
    setVerifiedWorkspaceSlug('');
    setTenantName('');
    setTenantBranding(null);
    setError(null);
    setErrorCode(null);
  };

  const handleSwitchWorkspace = () => {
    trackEvent('workspace_mismatch_switch_clicked');
    setError(null);
    setErrorCode(null);
    setStep('workspace');
    setVerifiedWorkspaceSlug('');
    setWorkspace('');
    setTenantName('');
    setTenantBranding(null);
    setIsPlatformAdmin(false);
  };

  const handleMagicLink = async () => {
    if (!email.trim()) {
      setError('Please enter your email address first');
      return;
    }
    setError(null);
    setMagicLinkSending(true);
    setMagicLinkSent(false);
    try {
      const { getTenantHeaders } = await import('../utils/apiClient');
      const activeTenantSlug = isPlatformAdmin ? null : (verifiedWorkspaceSlug || workspace.trim().toLowerCase());
      let tenantHeaders = {};
      if (activeTenantSlug) {
        tenantHeaders = { 'X-Tenant-Subdomain': activeTenantSlug };
      } else if (!isPlatformAdmin) {
        tenantHeaders = getTenantHeaders();
        delete tenantHeaders['Content-Type'];
      }

      const response = await fetch(`${configs.BACKEND_URL}/api/user/magic-link/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...tenantHeaders },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to send sign-in link.');
      }

      setMagicLinkSent(true);
      trackEvent('magic_link_requested', { is_platform_admin: isPlatformAdmin });
    } catch (err) {
      setError(err.message || 'Failed to send sign-in link. Please try again.');
    } finally {
      setMagicLinkSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setErrorCode(null);
    setIsLoading(true);

    const startTime = Date.now();
    const activeTenantSlug = isPlatformAdmin ? null : (verifiedWorkspaceSlug || workspace.trim().toLowerCase());
    const flowVariant = tenantSlug ? FLOW_VARIANTS.DIRECT_LOGIN : 
                       (verifiedWorkspaceSlug ? FLOW_VARIANTS.FAST_PATH : FLOW_VARIANTS.WORKSPACE_FIRST);

    // Track login started
    trackAuthEvent(AUTH_LOGIN_STARTED, {
      tenantId: activeTenantSlug,
      flowVariant: flowVariant,
    });

    try {
      // Set or clear tenant context before login
      if (isPlatformAdmin) {
        try { localStorage.removeItem('tenant-slug'); } catch {}
        await login(email, password, null);
      } else {
        if (!activeTenantSlug) {
          throw new Error('Please verify your workspace before signing in.');
        }
        try { localStorage.setItem('tenant-slug', activeTenantSlug); } catch {}
        await login(email, password, activeTenantSlug);
      }

      const currentUser = useAuthStore.getState().user;
      const durationMs = Date.now() - startTime;

      // Track login succeeded
      trackAuthEvent(AUTH_LOGIN_SUCCEEDED, {
        userId: currentUser?._id,
        role: currentUser?.role,
        tenantId: activeTenantSlug,
        flowVariant: flowVariant,
        durationMs: durationMs,
      });

      // Legacy event for backwards compatibility
      trackEvent('user_logged_in', {
        role: currentUser?.role,
        is_platform_admin: isPlatformAdmin,
      });

      // Check for redirect query parameter (e.g., from OAuth flow)
      const redirectParam = new URLSearchParams(location.search).get('redirect');
      if (redirectParam) {
        if (redirectParam.startsWith('http')) {
          window.location.href = redirectParam; // External URL (e.g., server OAuth endpoint)
        } else if (redirectParam.startsWith('/')) {
          navigate(redirectParam, { replace: true });
        }
      } else if (currentUser) {
        if (currentUser.role === 'owner') {
          navigate('/owner/tenants', { replace: true });
        } else if (currentUser.role === Roles.ADMIN) {
          navigate('/admin', { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      const durationMs = Date.now() - startTime;

      // Track login failed
      trackAuthEvent(AUTH_LOGIN_FAILED, {
        tenantId: activeTenantSlug,
        flowVariant: flowVariant,
        errorCode: err?.code || 'UNKNOWN_ERROR',
        durationMs: durationMs,
      });

      // Legacy event for backwards compatibility
      trackEvent('login_failed', { 
        is_platform_admin: isPlatformAdmin, 
        code: err?.code,
        has_mismatch: err?.code === 'WORKSPACE_MISMATCH',
      });

      setErrorCode(err?.code || null);
      setError(err instanceof Error ? err.message : 'An error occurred during sign in');
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasHydrated) return null;
  if (isAuthenticated && user) return null;

  // URL-based tenant slug is being verified OR stored tenant is being auto-verified
  if (isVerifying && (tenantSlug || step === 'workspace')) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-5 h-5 animate-spin" />
          Verifying workspace...
        </div>
      </div>
    );
  }

  // URL-based tenant slug failed verification — show error with option to go to login
  if (tenantSlug && error && step === 'workspace') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <Link to="/" className="flex items-center space-x-2 mb-2">
              <PropelLogo />
            </Link>
            <span className="text-xl font-heading font-bold text-gray-900">Propel</span>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Go to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          {step === 'credentials' && tenantBranding?.logo ? (
            <img src={tenantBranding.logo} alt={tenantName || 'Logo'} className="h-10 w-auto max-w-[160px] object-contain mb-2" />
          ) : (
            <>
              <Link to="/" className="flex items-center space-x-2 mb-2">
                <PropelLogo />
              </Link>
              <span className="text-xl font-heading font-bold text-gray-900">Propel</span>
            </>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {step === 'workspace' ? (
            <>
              <h2 className="text-xl font-semibold text-center text-gray-900 mb-2">
                Sign in to your workspace
              </h2>
              <p className="text-sm text-center text-gray-500 mb-8">
                Enter your workspace name to continue
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg mb-6 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleContinue} className="space-y-6">
                <div>
                  <label htmlFor="workspace" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Workspace
                  </label>
                  <div className="flex items-stretch border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all">
                    <input
                      id="workspace"
                      type="text"
                      value={workspace}
                      onChange={(e) => setWorkspace(e.target.value)}
                      placeholder="your-workspace"
                      className="flex-1 px-3 py-2.5 text-sm border-none focus:outline-none focus:ring-0"
                      autoFocus
                    />
                    <span className="inline-flex items-center px-3 bg-gray-50 text-gray-400 text-sm border-l border-gray-300 select-none">
                      .propel.app
                    </span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isVerifying}
                  className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Continue'
                  )}
                </button>
              </form>

              <div className="mt-8">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-3 text-gray-400">or</span>
                  </div>
                </div>

                <button
                  onClick={handlePlatformAdmin}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <Shield className="w-4 h-4" />
                  Platform admin? Sign in without a workspace
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Back button */}
              {!tenantSlug && (
                <button
                  onClick={handleBack}
                  className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </button>
              )}

              {/* Workspace badge */}
              <div className="flex justify-center mb-6">
                {isPlatformAdmin ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                    <Shield className="w-3.5 h-3.5" />
                    Platform Admin
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm font-medium">
                    {tenantName ? `${tenantName} · ` : ''}
                    {(verifiedWorkspaceSlug || workspace || '').toLowerCase()}.propel.app
                  </span>
                )}
              </div>

              <h2 className="text-xl font-semibold text-center text-gray-900 mb-2">
                Welcome back
              </h2>
              <p className="text-sm text-center text-gray-500 mb-8">
                Enter your credentials to sign in
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg mb-6 text-sm">
                  <p className="font-medium mb-1">{error}</p>
                  {errorCode === 'WORKSPACE_MISMATCH' && !isPlatformAdmin && (
                    <>
                      <p className="text-xs mb-3 text-red-500">
                        Your account is not associated with this workspace. You can switch to a different workspace or contact your administrator.
                      </p>
                      <button
                        type="button"
                        onClick={handleSwitchWorkspace}
                        className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-white border border-red-300 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 transition-colors"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Switch Workspace
                      </button>
                    </>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full rounded-lg border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2.5 text-sm transition-colors"
                    autoFocus
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <Link
                      to={forgotPasswordPath}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full rounded-lg border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2.5 text-sm transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              {/* Magic link option */}
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-3 text-gray-400">or</span>
                  </div>
                </div>

                {magicLinkSent ? (
                  <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-lg text-sm text-center">
                    <p className="font-medium">Check your email!</p>
                    <p className="text-xs mt-1 text-green-600">If an account exists, we sent a sign-in link. It expires in 15 minutes.</p>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleMagicLink}
                    disabled={magicLinkSending}
                    className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {magicLinkSending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Email me a sign-in link
                      </>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          &copy; {new Date().getFullYear()} Propel. All rights reserved.
        </p>
      </div>
    </div>
  );
}
